/**
 * Identifier Detection Utilities
 *
 * Pure functions for detecting identifier patterns (UEI, DUNS, CAGE) in search input.
 * Extracted from RecipientToolbar for reuse in auto-lookup and badge rendering.
 */
import type { IdentifierType } from '@/types/recipient-search'

export interface PrefixTrigger {
  prefix: string
  type: IdentifierType
  label: string
  example: string
  pattern: RegExp
}

// Patterns: UEI = 12 alphanumeric, DUNS = 9 digits, CAGE = 5 alphanumeric
export const PREFIX_TRIGGERS: PrefixTrigger[] = [
  {
    prefix: 'uei:',
    type: 'uei',
    label: 'Unique Entity ID',
    example: 'J7M9HPTGJ1S9',
    pattern: /^[A-Z0-9]{12}$/i,
  },
  {
    prefix: 'duns:',
    type: 'duns',
    label: 'D-U-N-S Number',
    example: '123456789',
    pattern: /^\d{9}$/,
  },
  {
    prefix: 'cage:',
    type: 'cage',
    label: 'CAGE Code',
    example: '1ABC2',
    pattern: /^[A-Z0-9]{5}$/i,
  },
]

/** Detect if input exactly matches an identifier pattern */
export function detectIdentifierPattern(value: string): IdentifierType | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  for (const trigger of PREFIX_TRIGGERS) {
    if (trigger.pattern.test(trimmed)) {
      return trigger.type
    }
  }
  return null
}

/** Detect if input matches a prefix keyword (without the colon) */
export function detectPrefixKeyword(value: string): IdentifierType | null {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return null

  for (const trigger of PREFIX_TRIGGERS) {
    // Match the prefix without the colon (e.g., "duns" matches "duns:")
    const prefixWithoutColon = trigger.prefix.slice(0, -1)
    if (trimmed === prefixWithoutColon) {
      return trigger.type
    }
  }
  return null
}
